import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, ImagePlus, Trash2 } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { formatBytes } from '@/lib/format'
import { queryKeys } from '@/lib/query-client'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { useToast } from '@/components/ui/useToast'
import { createDriver, uploadDriverPhotoWithProgress } from './api'
import styles from './AddDriverDialog.module.css'

/** Mirrors the server's own limits, so a rejection is rare and explained early. */
const MAX_PHOTO_BYTES = 2 * 1024 * 1024
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

const schema = z.object({
  name: z.string().trim().min(2, 'Enter the driver’s full name').max(100, 'Name must be 100 characters or fewer'),
  email: z.string().trim().min(1, 'Enter an email address').email('Enter a valid email address'),
  phone: z
    .string()
    .trim()
    .min(8, 'Enter a phone number including the country code')
    .max(20, 'Phone number is too long')
    .regex(/^\+?[0-9\s()-]+$/, 'Use digits, and optionally a leading +'),
  password: z
    .string()
    .min(6, 'Password must be at least 6 characters')
    .max(72, 'Password must be 72 characters or fewer'),
})

type DriverValues = z.infer<typeof schema>

interface AddDriverDialogProps {
  open: boolean
  onClose: () => void
}

type Stage = 'idle' | 'uploading' | 'creating'

export function AddDriverDialog({ open, onClose }: AddDriverDialogProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [photo, setPhoto] = useState<File | null>(null)
  const [preview, setPreview] = useState<string | null>(null)
  const [photoError, setPhotoError] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState<Stage>('idle')
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<DriverValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', phone: '', password: '' },
  })

  // Object URLs are a real allocation; releasing them keeps a long admin
  // session from accumulating image blobs.
  useEffect(() => {
    if (!photo) {
      setPreview(null)
      return
    }
    const url = URL.createObjectURL(photo)
    setPreview(url)
    return () => URL.revokeObjectURL(url)
  }, [photo])

  useEffect(() => {
    if (open) return
    reset()
    setPhoto(null)
    setPhotoError(null)
    setProgress(0)
    setStage('idle')
    setShowPassword(false)
  }, [open, reset])

  const selectPhoto = (file: File | undefined) => {
    setPhotoError(null)

    if (!file) {
      setPhoto(null)
      return
    }
    if (!ACCEPTED_TYPES.includes(file.type.toLowerCase())) {
      setPhotoError('Choose a JPG, PNG, WebP or GIF image.')
      setPhoto(null)
      return
    }
    if (file.size > MAX_PHOTO_BYTES) {
      setPhotoError(`That image is ${formatBytes(file.size)}. The limit is 2 MB.`)
      setPhoto(null)
      return
    }
    setPhoto(file)
  }

  const busy = stage !== 'idle'

  const onSubmit = async (values: DriverValues) => {
    let profileImageUrl: string | undefined

    try {
      // The photo is stored first: if it fails there is no half-created driver
      // account left behind with a broken image reference.
      if (photo) {
        setStage('uploading')
        setProgress(0)
        const uploaded = await uploadDriverPhotoWithProgress(photo, setProgress)
        profileImageUrl = uploaded.url
      }

      setStage('creating')
      await createDriver({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        password: values.password,
        ...(profileImageUrl ? { profileImageUrl } : {}),
      })

      toast.success('Driver added', `${values.name.trim()} can now sign in to the driver app.`)
      void queryClient.invalidateQueries({ queryKey: queryKeys.drivers.all })
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      onClose()
    } catch (error) {
      if (error instanceof ApiError) {
        // Field-level messages belong on the field that caused them.
        if (error.fieldErrors) {
          for (const [field, message] of Object.entries(error.fieldErrors)) {
            if (field === 'name' || field === 'email' || field === 'phone' || field === 'password') {
              setError(field, { message })
            }
          }
          toast.error('Check the highlighted fields', error.message)
        } else if (/phone/i.test(error.message)) {
          setError('phone', { message: error.message })
          toast.error('Could not add the driver', error.message)
        } else if (/email/i.test(error.message)) {
          setError('email', { message: error.message })
          toast.error('Could not add the driver', error.message)
        } else {
          toast.error('Could not add the driver', error.message)
        }
      } else {
        toast.error('Could not add the driver', 'An unexpected error occurred.')
      }
    } finally {
      setStage('idle')
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add a driver"
      description="Creates an enabled driver account with a verified phone number, ready to sign in to the driver app."
      size="lg"
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" form="add-driver-form" type="submit" loading={busy}>
            {stage === 'uploading' ? 'Uploading photo…' : stage === 'creating' ? 'Creating…' : 'Add driver'}
          </Button>
        </>
      }
    >
      <form id="add-driver-form" className={styles.form} onSubmit={(event) => void handleSubmit(onSubmit)(event)} noValidate>
        <div className={styles.photoRow}>
          <div className={styles.preview}>
            {preview ? (
              <img src={preview} alt="Selected driver photo preview" />
            ) : (
              <span className={styles.previewEmpty}>
                <ImagePlus aria-hidden="true" />
              </span>
            )}
          </div>

          <div className={styles.photoControls}>
            <p className={styles.photoLabel}>Profile photo</p>
            <p className={styles.photoHint}>
              Square images look best. JPG, PNG, WebP or GIF, up to 2&nbsp;MB. Optional.
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES.join(',')}
              className="visually-hidden"
              onChange={(event) => selectPhoto(event.target.files?.[0])}
              disabled={busy}
              aria-label="Driver profile photo"
            />

            <div className={styles.photoButtons}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={busy}
                iconLeft={<ImagePlus aria-hidden="true" />}
              >
                {photo ? 'Choose another' : 'Choose image'}
              </Button>

              {photo ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPhoto(null)
                    if (fileInputRef.current) fileInputRef.current.value = ''
                  }}
                  disabled={busy}
                  iconLeft={<Trash2 aria-hidden="true" />}
                >
                  Remove
                </Button>
              ) : null}
            </div>

            {photo ? (
              <p className={styles.photoMeta}>
                {photo.name} · {formatBytes(photo.size)}
              </p>
            ) : null}

            {stage === 'uploading' ? (
              <div className={styles.progress}>
                <div
                  className={styles.progressTrack}
                  role="progressbar"
                  aria-valuenow={progress}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label="Photo upload progress"
                >
                  <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                </div>
                <span className={styles.progressValue}>{progress}%</span>
              </div>
            ) : null}

            {photoError ? (
              <p className={styles.photoError} role="alert">
                {photoError}
              </p>
            ) : null}
          </div>
        </div>

        <div className={styles.grid}>
          <Field label="Full name" error={errors.name?.message} required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                placeholder="Ahmed Hassan"
                autoComplete="off"
                disabled={busy}
                {...register('name')}
              />
            )}
          </Field>

          <Field label="Phone number" error={errors.phone?.message} hint="Include the country code." required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                placeholder="+201001234567"
                inputMode="tel"
                autoComplete="off"
                disabled={busy}
                {...register('phone')}
              />
            )}
          </Field>

          <Field label="Email address" error={errors.email?.message} required>
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type="email"
                placeholder="driver@driveforme.com"
                autoComplete="off"
                disabled={busy}
                {...register('email')}
              />
            )}
          </Field>

          <Field
            label="Temporary password"
            error={errors.password?.message}
            hint="Share this with the driver; they can change it in the app."
            required
          >
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 6 characters"
                autoComplete="new-password"
                disabled={busy}
                addonRight={
                  <button
                    type="button"
                    className={styles.reveal}
                    onClick={() => setShowPassword((current) => !current)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                  </button>
                }
                {...register('password')}
              />
            )}
          </Field>
        </div>
      </form>
    </Dialog>
  )
}
